import { useMemo, useState } from 'react'
import { PageContainer } from '@/components/Container'
import { ChevronLeft, ChevronDown, ChevronUp } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useGameRegistry } from '@/hooks/useGameRegistry'
import { useSections } from '@/hooks/useSections'
import { GameIcon } from '@/utils/iconRenderer'
import type { Game } from '@/types/game'

// Character limit for truncated description
const DESCRIPTION_CHAR_LIMIT = 150

// Component for individual game help section
function GameHelpSection({ game }: { game: Game }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const iconUrl = game.iconUrl || game.displayName.substring(0, 2).toUpperCase()

  // Get the description text
  const fullDescription = game.configuration.helpText ||
    `${game.displayName} - ${game.tagline}. Check back soon for more detailed instructions on how to play!`

  // Check if we need to truncate
  const needsTruncation = fullDescription.length > DESCRIPTION_CHAR_LIMIT

  // Get truncated version (cut at word boundary)
  const truncatedDescription = useMemo(() => {
    if (!needsTruncation) return fullDescription
    const truncated = fullDescription.substring(0, DESCRIPTION_CHAR_LIMIT)
    // Cut at last space to avoid cutting words
    const lastSpace = truncated.lastIndexOf(' ')
    return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...'
  }, [fullDescription, needsTruncation])

  const displayedDescription = isExpanded ? fullDescription : truncatedDescription

  return (
    <section className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <GameIcon
          iconUrl={iconUrl}
          backgroundColor={game.signatureColor}
          size={48}
          alt={game.displayName}
        />
        <div>
          <h2 className="font-bebas-neue text-xl md:text-2xl tracking-[1.5px] uppercase text-foreground">
            {game.displayName}
          </h2>
          <p className="font-oswald text-xs md:text-sm font-medium text-muted-foreground uppercase tracking-[0.5px]">
            {game.tagline}
          </p>
        </div>
      </div>
      <div className="space-y-3 font-oswald text-sm md:text-base text-foreground/80">
        <div id={`description-${game.id}`} className="space-y-2">
          {displayedDescription.split('\n\n').map((para, i) => (
            <p key={i}>
              {para.split('\n').map((line, j, arr) => (
                <span key={j}>{line}{j < arr.length - 1 && <br />}</span>
              ))}
            </p>
          ))}
        </div>

        {/* Show More/Less button */}
        {needsTruncation && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            aria-controls={`description-${game.id}`}
            className="flex items-center gap-1 text-primary hover:text-primary/80 font-medium transition-colors"
          >
            <span>{isExpanded ? 'Show Less' : 'More'}</span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        )}
      </div>
    </section>
  )
}

export default function Help() {
  const { games, loading: gamesLoading } = useGameRegistry()
  const { sections, isLoading: sectionsLoading } = useSections()

  // Get games in the same order as they appear on the main page (by section order)
  const orderedGames = useMemo(() => {
    if (!sections || !games.length) return []

    // Build ordered list of game IDs from sections
    const orderedGameIds: string[] = []
    sections.forEach(section => {
      section.games.forEach(sectionGame => {
        if (!orderedGameIds.includes(sectionGame.id)) {
          orderedGameIds.push(sectionGame.id)
        }
      })
    })

    // Map to full game objects, filter to published only
    return orderedGameIds
      .map(id => games.find(g => g.id === id))
      .filter((game): game is Game => game !== undefined && game.isPublished)
  }, [sections, games])

  const loading = gamesLoading || sectionsLoading

  return (
    <PageContainer>
      <div className="space-y-4 md:space-y-4 xl:space-y-6">
        {/* Page Header */}
        <section className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Link
              to="/"
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="font-oswald text-sm font-medium uppercase tracking-[0.5px]">
                Back
              </span>
            </Link>
          </div>
          <h1 className="font-bebas-neue text-[clamp(24px,5vw,40px)] xl:text-[clamp(28px,2vw,36px)] font-medium tracking-[2px] uppercase text-foreground">
            Help and Information
          </h1>
          <p className="font-oswald text-[clamp(11px,2.5vw,14px)] xl:text-[clamp(12px,0.8vw,14px)] font-medium text-muted-foreground uppercase tracking-[0.5px] mt-1">
            Learn how to play our games and make the most of your experience
          </p>
        </section>

        {/* Comments & Feedback Section */}
        <section className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-6 shadow-sm">
          <h2 className="font-bebas-neue text-xl md:text-2xl tracking-[1.5px] uppercase text-foreground mb-3">
            Comments & Feedback
          </h2>
          <div className="font-oswald text-sm md:text-base text-foreground/80">
            <p>
              We'd love to hear from you! If you have any comments, suggestions, or ideas for improvements, please email us at{' '}
              <a
                href="mailto:support@imaginesports.com"
                className="text-primary hover:underline"
              >
                support@imaginesports.com
              </a>
              . Your feedback helps us make the games better for everyone.
            </p>
          </div>
        </section>

        {/* Game Sections - pulled from registry (all published games) */}
        {loading ? (
          // Loading skeleton
          <>
            {[1, 2, 3].map(i => (
              <section key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-6 shadow-sm animate-pulse">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-600" />
                  <div className="flex-1">
                    <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-1/3 mb-2" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/2" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-full" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-5/6" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-4/6" />
                </div>
              </section>
            ))}
          </>
        ) : (
          orderedGames.map(game => (
            <GameHelpSection key={game.id} game={game} />
          ))
        )}

        {/* General Help Section */}
        <section className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-6 shadow-sm">
          <h2 className="font-bebas-neue text-xl md:text-2xl tracking-[1.5px] uppercase text-foreground mb-4">
            General Information
          </h2>
          <div className="space-y-4 font-oswald text-sm md:text-base text-foreground/80">
            <div>
              <h3 className="font-bold text-foreground mb-1">Daily Challenges</h3>
              <p>
                New challenges are released daily at midnight Eastern Time. You can play today's challenge and view your progress on past challenges in the archive.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-foreground mb-1">Progress Tracking</h3>
              <p>
                Your progress is automatically saved when you're signed in. View your stats, streaks, and completion history on the Games page.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-foreground mb-1">Premium Features</h3>
              <p>
                Premium subscribers get access to the full archive of past challenges. Free users can play today's challenge and the most recent week.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-foreground mb-1">Need More Help?</h3>
              <p>
                Contact us at{' '}
                <a
                  href="mailto:support@imaginesports.com"
                  className="text-primary hover:underline"
                >
                  support@imaginesports.com
                </a>{' '}
                for additional assistance.
              </p>
            </div>
          </div>
        </section>
      </div>
    </PageContainer>
  )
}
